import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import { StoredFeatureTemplate, featureTemplateApi } from "../api/featureTemplate";

const LOCAL_STORAGE_KEY = "featureTemplates";

interface UseFeatureTemplatesReturn {
  templates: StoredFeatureTemplate[];
  isLoading: boolean;
  saveTemplates: (templates: StoredFeatureTemplate[]) => Promise<void>;
}

/**
 * Hook to manage feature templates with optimistic updates
 *
 */
export function useFeatureTemplates(): UseFeatureTemplatesReturn {
  const [templates, setTemplates] = useState<StoredFeatureTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load templates on mount
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);

        if (stored) {
          // localStorage exists - use it immediately
          const parsedTemplates: StoredFeatureTemplate[] = JSON.parse(stored);

          // Check database to see if we should sync
          const dbTemplates = await featureTemplateApi.getAll();

          if (dbTemplates.length > 0) {
            // Database has data - sync FROM database to localStorage
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dbTemplates));
            setTemplates(dbTemplates);
          } else {
            // Database is empty - use localStorage data
            setTemplates(parsedTemplates);
          }

          setIsLoading(false);
        } else {
          // No localStorage - check database
          const dbTemplates = await featureTemplateApi.getAll();

          if (dbTemplates.length > 0) {
            // Database has data - sync to localStorage
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(dbTemplates));
            setTemplates(dbTemplates);
          } else {
            // No data anywhere - start with empty
            setTemplates([]);
          }

          setIsLoading(false);
        }
      } catch (error) {
        // If API fails, use localStorage if available
        const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (stored) {
          const parsedTemplates: StoredFeatureTemplate[] = JSON.parse(stored);
          setTemplates(parsedTemplates);
        } else {
          setTemplates([]);
        }
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Save templates with optimistic update
  const saveTemplates = async (newTemplates: StoredFeatureTemplate[]) => {
    // Store previous state for rollback
    const previousTemplates = templates;

    try {
      // Optimistic update: Update localStorage and state immediately
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(newTemplates));
      setTemplates(newTemplates);

      // Sync ALL templates to database (import replaces all data)
      // We need to send all templates, not just the new ones
      await featureTemplateApi.import(newTemplates);
    } catch (error) {
      // Rollback on error
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(previousTemplates));
      setTemplates(previousTemplates);

      toast.error("Failed to sync templates to database");
    }
  };

  return {
    templates,
    isLoading,
    saveTemplates
  };
}
